import { Dispatch, FC, Fragment, useEffect, useState, useCallback, useRef } from 'react';
import { Transition } from '@headlessui/react';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Flex from '../../../layout/Flex.tsx';
import Icon from '../../../Icon.tsx';
import SecondaryText from '../../../typography/SecondaryText.tsx';
import IconButton from '../../../buttons/IconButton.tsx';

import { ISnackbar, SnackbarStatus } from '../../../../constants/types/prop.types.ts';
import { IconKeys } from '../../../constants/icons.ts';

type Props = Omit<ISnackbar, 'id'> & {
  onClose: Dispatch<void>;
};

const Icons = {
  [SnackbarStatus.Error]: IconKeys.XCircle,
  [SnackbarStatus.Success]: IconKeys.CircleCheckFilled,
  [SnackbarStatus.Info]: IconKeys.Info,
  [SnackbarStatus.Warning]: IconKeys.Warning,
};

const Snackbar: FC<Props> = ({
  text,
  status = SnackbarStatus.Error,
  onClose,
  timeout = 4000,
  onClick,
}) => {
  const { t } = useTranslation('component/snackbar');
  const [isOpen, setIsOpen] = useState(false);
  const openTimerRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const transitionTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleClose = useCallback(() => {
    setIsOpen(false);

    // Clear any existing transition timer
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
    }

    // Set new transition timer
    transitionTimerRef.current = setTimeout(() => {
      onClose();
      transitionTimerRef.current = null;
    }, 1000);
  }, [onClose]);

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
      handleClose(); // Close the snackbar after click
    }
  }, [onClick, handleClose]);

  const handleCloseClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent triggering onClick when close button is clicked
      handleClose();
    },
    [handleClose],
  );

  useEffect(() => {
    // Clear any existing timers
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);

    // Set timer to open the snackbar
    openTimerRef.current = setTimeout(() => {
      setIsOpen(true);
      openTimerRef.current = null;
    }, 0);

    // Set timer to auto-close the snackbar
    closeTimerRef.current = setTimeout(() => {
      handleClose();
      closeTimerRef.current = null;
    }, timeout);

    // Cleanup function to clear all timers
    return () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = null;
      }
    };
  }, [timeout, handleClose]);

  return (
    <Transition
      as={Fragment}
      show={isOpen}
      enter="ease-out duration-300"
      enterFrom="opacity-0 scale-95"
      enterTo="opacity-100 scale-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100 scale-100"
      leaveTo="opacity-0 scale-95"
    >
      <Flex.Wrapper
        gap="26px"
        jBetween
        className={classNames(
          'p-[12px_16px] bg-black rounded-sm max-w-[530px]',
          onClick && 'cursor-pointer hover:bg-gray-800 transition-colors',
        )}
        onClick={handleClick}
      >
        <Flex.Wrapper gap="8px">
          <Icon
            iconKey={Icons[status]}
            className={classNames({
              'text-system-200': status === SnackbarStatus.Error,
              'text-system-100': status === SnackbarStatus.Success,
              'text-blue-400': status === SnackbarStatus.Info,
              'text-yellow-400': status === SnackbarStatus.Warning,
            })}
          />

          <SecondaryText
            className="text-white"
            style={{ whiteSpace: 'pre-wrap', lineBreak: 'anywhere' }}
          >
            {t(text)}
          </SecondaryText>
        </Flex.Wrapper>

        <IconButton
          icon={IconKeys.Close}
          className="!text-gray-400 after:hover:!bg-white/20"
          onClick={handleCloseClick}
        />
      </Flex.Wrapper>
    </Transition>
  );
};

export default Snackbar;
